<script lang="ts">
  import { wbsLabel } from "../lib/wbs";
  import { tasks, det, wbsNodes, projectStartDate, allowWeekendWork, projectPhases } from "../lib/stores";
  import { phaseColor } from "../lib/phases";
  import { axisDateLabel, isWeekendDay, projectStartDay, weekOfProjectDay } from "../lib/projectDate";
  import { milestoneDisplayDay } from "../lib/cpm";
  import type { Phase, TaskType } from "../lib/types";

  const LABEL_W = 210; // px — must match .grow-label width / .axis-row margin-left in app.css
  const ROW_H = 24; // px — must match .grow-row / .phase-header height in app.css

  interface Row {
    id: string; wbs: string; name: string; phase: Phase; type: TaskType;
    es: number; ef: number; critical: boolean; preds: string[];
    /** Where a milestone diamond is actually drawn — the day its predecessors
     *  finished, not the CPM boundary `es` sits on (which links still use, so
     *  arrows keep connecting to the right edge of the preceding bar). */
    markDay: number;
  }

  interface Link { x1: number; y1: number; xMid: number; x2: number; y2: number; critical: boolean; }

  interface WeekLabel { day: number; weekNum: number; span: number }

  type Item =
    | { kind: "header"; phase: Phase; count: number }
    | { kind: "task"; row: Row };

  // Everything the template needs is resolved here, once, into a plain array —
  // avoids relying on TypeScript narrowing $det.ok across repeated store reads
  // in the markup (unverifiable in this environment, so kept as simple as possible).
  let axisMax = 10;
  let step = 5;
  let ticks: number[] = [];
  let rows: Row[] = [];
  let weekendBands: number[] = []; // start day of each weekend block within [0, axisMax]
  let weekLabels: WeekLabel[] = [];

  $: {
    const result = $det;
    if (result.ok) {
      axisMax = Math.max(10, Math.ceil((result.projectEnd + 4) / 5) * 5);
      step = axisMax <= 30 ? 5 : axisMax <= 70 ? 10 : 20;
      const t2 = [];
      for (let d = 0; d <= axisMax; d += step) t2.push(d);
      ticks = t2;

      const bands: number[] = [];
      for (let d = 0; d < axisMax; d++) if (isWeekendDay(d) && !isWeekendDay(d - 1)) bands.push(d);
      weekendBands = bands;

      const wls: WeekLabel[] = [];
      for (let d = 0; d < axisMax; d += 7) wls.push({ day: d, weekNum: weekOfProjectDay(d), span: Math.min(7, axisMax - d) });
      weekLabels = wls;

      // A phase that's since been renamed/removed from the project's swimlanes sorts
      // after every known one, rather than crashing on a -1 index or vanishing.
      const phaseIdx: Record<string, number> = {};
      $projectPhases.forEach((p, i) => (phaseIdx[p.name] = i));
      const order = [...$tasks].sort((a, b) => {
        const pa = phaseIdx[a.phase] ?? Infinity, pb = phaseIdx[b.phase] ?? Infinity;
        if (pa !== pb) return pa - pb;
        return result.ES[a.id] - result.ES[b.id];
      });
      rows = order.map((t) => ({
        id: t.id, wbs: wbsLabel(t, $wbsNodes), name: t.name, phase: t.phase, type: t.type,
        es: result.ES[t.id], ef: result.EF[t.id], critical: result.critical[t.id], preds: t.preds,
        markDay: t.type === "milestone"
          ? milestoneDisplayDay(t, result.ES, result.EF, $allowWeekendWork)
          : result.ES[t.id],
      }));
    } else {
      ticks = [];
      rows = [];
      weekendBands = [];
      weekLabels = [];
    }
  }

  function pct(d: number): number { return (d / axisMax) * 100; }
  function tickLabel(d: number): string { return axisDateLabel($projectStartDate, d) ?? `d${d}`; }
  $: startDay = projectStartDay($projectStartDate);

  // Rows are already contiguous by phase (sorted above), so a single pass groups them.
  $: groups = ((): { phase: Phase; rows: Row[] }[] => {
    const gs: { phase: Phase; rows: Row[] }[] = [];
    for (const r of rows) {
      const last = gs[gs.length - 1];
      if (last && last.phase === r.phase) last.rows.push(r);
      else gs.push({ phase: r.phase, rows: [r] });
    }
    return gs;
  })();

  let collapsedPhases = new Set<Phase>();
  function togglePhase(phase: Phase) {
    const next = new Set(collapsedPhases);
    if (next.has(phase)) next.delete(phase); else next.add(phase);
    collapsedPhases = next;
  }

  // Flattened render list — a header item per phase group, followed by its task rows
  // unless that phase is collapsed. This is also the coordinate space the link layer
  // and weekend bands are drawn in, so a collapsed phase's rows (and any links touching
  // them) simply drop out rather than needing separate hide/show bookkeeping.
  $: items = ((): Item[] => {
    const out: Item[] = [];
    for (const g of groups) {
      out.push({ kind: "header", phase: g.phase, count: g.rows.length });
      if (!collapsedPhases.has(g.phase)) for (const r of g.rows) out.push({ kind: "task", row: r });
    }
    return out;
  })();

  // Link layer: drawn in real pixels (not %) so elbow segments and arrowheads render
  // undistorted — bind:clientWidth gives the actual rendered track width to convert against.
  let growWidth = 0;
  $: trackW = Math.max(0, growWidth - LABEL_W);
  $: totalH = items.length * ROW_H;
  $: links = ((): Link[] => {
    if (!trackW || !items.length) return [];
    const posById = new Map<string, { es: number; ef: number; critical: boolean; cy: number }>();
    items.forEach((it, i) => {
      if (it.kind === "task") posById.set(it.row.id, { es: it.row.es, ef: it.row.ef, critical: it.row.critical, cy: i * ROW_H + ROW_H / 2 });
    });
    const out: Link[] = [];
    for (const it of items) {
      if (it.kind !== "task") continue;
      const succ = posById.get(it.row.id)!;
      for (const predId of it.row.preds) {
        const p = posById.get(predId);
        if (!p) continue;
        const x1 = (p.ef / axisMax) * trackW;
        const x2 = (succ.es / axisMax) * trackW;
        const xMid = x1 + Math.min(8, Math.max(0, x2 - x1));
        out.push({ x1, y1: p.cy, xMid, x2, y2: succ.cy, critical: p.critical && it.row.critical });
      }
    }
    return out;
  })();
</script>

<div class="chart-frame">
  <div id="ganttScroll">
    <div class="grow" bind:clientWidth={growWidth}>
      <div class="week-row" id="weekRow">
        {#each weekendBands as d}
          <div class="weekend-band" style="left:{pct(d)}%; width:{pct(Math.min(2, axisMax - d))}%; height:100%;"></div>
        {/each}
        {#each weekLabels as w}
          <div class="week-cell" style="left:{pct(w.day)}%; width:{pct(w.span)}%">
            <span class="week-label">Week {w.weekNum}</span>
          </div>
        {/each}
      </div>
      <div class="axis-row" id="axisRow">
        {#each weekendBands as d}
          <div class="weekend-band" style="left:{pct(d)}%; width:{pct(Math.min(2, axisMax - d))}%; height:100%;"></div>
        {/each}
        {#each ticks as d}
          <div class="axis-tick" style="left:{pct(d)}%">{tickLabel(d)}</div>
        {/each}
        {#if startDay != null}
          <div class="axis-tick start-tick" style="left:{pct(startDay)}%">Start</div>
        {/if}
      </div>
      <div id="ganttRows">
        <div class="weekend-layer" style="height:{totalH}px;">
          {#each weekendBands as d}
            <div class="weekend-band" style="left:{pct(d)}%; width:{pct(Math.min(2, axisMax - d))}%; height:100%;"></div>
          {/each}
          {#if startDay != null}
            <div class="start-marker" style="left:{pct(startDay)}%"></div>
          {/if}
        </div>
        {#each items as it (it.kind === "header" ? "h:" + it.phase : it.row.id)}
          {#if it.kind === "header"}
            <button type="button" class="phase-header" style="border-left-color:{phaseColor($projectPhases, it.phase)}"
              on:click={() => togglePhase(it.phase)} aria-expanded={!collapsedPhases.has(it.phase)}>
              <span class="phase-chevron" class:collapsed={collapsedPhases.has(it.phase)}>▾</span>
              <span class="phase-name">{it.phase}</span>
              <span class="phase-count">{it.count} {it.count === 1 ? "item" : "items"}</span>
            </button>
          {:else}
            <div class="grow-row">
              <div class="grow-label" title={it.row.name}><span class="pid">{it.row.wbs}</span>{it.row.name}</div>
              <div class="grow-track">
                {#if it.row.type === "milestone"}
                  <div class="gmilestone" class:critical={it.row.critical}
                    style="left:{pct(it.row.markDay)}%; {it.row.critical ? '' : 'background:' + phaseColor($projectPhases, it.row.phase) + ';'}">
                  </div>
                {:else}
                  <div class="gbar" class:critical={it.row.critical}
                    style="left:{pct(it.row.es)}%; width:{Math.max(pct(it.row.ef - it.row.es), 0.5)}%; background:{phaseColor($projectPhases, it.row.phase)};">
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        {/each}
        {#if links.length}
          <svg class="link-layer" width={trackW} height={totalH}>
            <defs>
              <marker id="gantt-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" class="glink-head" />
              </marker>
              <marker id="gantt-arrow-critical" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" class="glink-head critical" />
              </marker>
            </defs>
            {#each links as l}
              <path class="glink" class:critical={l.critical}
                d="M {l.x1} {l.y1} L {l.xMid} {l.y1} L {l.xMid} {l.y2} L {l.x2 - 6} {l.y2}"
                marker-end={l.critical ? "url(#gantt-arrow-critical)" : "url(#gantt-arrow)"} />
            {/each}
          </svg>
        {/if}
      </div>
    </div>
  </div>
  <div class="legend">
    {#each $projectPhases as p (p.name)}
      <div class="legend-item"><span class="legend-swatch" style="background:{p.color}"></span>{p.name}</div>
    {/each}
    <div class="legend-item"><span class="legend-swatch diamond" style="background:var(--s-event)"></span>Milestone</div>
    <div class="legend-item"><span class="legend-swatch crit"></span>Critical path (zero slack)</div>
    <div class="legend-item"><span class="legend-swatch weekend"></span>Weekend (non-working day)</div>
  </div>
</div>
